import { useEffect, useState } from "react";

// Which section of a long page the reader is currently in, for highlighting the
// jump nav. Lifted out of ServicePage when the resources page grew a jump nav of
// its own; it lives beside fragment-landing.js because both are page behaviour
// that belongs to no single page.
//
// `resetKey` is what the page is about, not the id list. Reproductive Risk and
// Prenatal Screening expose the same three sections, so keying the reset on the
// ids alone left the previous page's highlight in place — and because that id is
// still valid on the new page, it stays highlighted rather than failing visibly.
export default function useActiveSection(ids, resetKey) {
  const [activeId, setActiveId] = useState(ids[0]);
  const key = ids.join("|");

  useEffect(() => {
    const sectionIds = key.split("|");
    setActiveId(sectionIds[0]);

    if (typeof IntersectionObserver === "undefined") return undefined;

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id);
      },
      // Bias the band toward the top of the viewport so the highlighted entry
      // is the section being read, not whichever one happens to be tallest.
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [key, resetKey]);

  return activeId;
}
